import React, { useState, useRef, useEffect } from 'react';
import { Outlet, NavLink, useLocation, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useRights } from '../hooks/useRights';
import { useModal } from '../context/ModalContext';
import { User, LogOut, Globe, UserCircle, ChevronDown } from 'lucide-react';
import './BOLayout.css';

const BOLayout: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user, userType, logout, selectedSchool } = useAuth();
  const { hasRight, hasAnyRight, isSuperAdmin } = useRights();
  const schoolId = localStorage.getItem('schoolId');
  const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileLanguageOpen, setMobileLanguageOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setLanguageDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileLanguageOpen(false);
  }, [location.pathname]);

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    setLanguageDropdownOpen(false);
    setMobileLanguageOpen(false);
  };

  const currentLanguage = i18n.language || 'fr';

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setMobileLanguageOpen(false);
  };

  // If no school selected yet, handle routing
  if (!schoolId) {
    // If we are on a school management route, render the content
    if (location.pathname.startsWith('/schools')) {
      return <div className="bo-layout-simple"><Outlet /></div>;
    }
    // Otherwise redirect to schools list
    return <Navigate to="/schools" replace />;
  }

  return (
    <div className="bo-layout">
      {/* Mobile-only header with just hamburger */}
      <div className="mobile-header">
        <button
          className={`hamburger-btn ${mobileMenuOpen ? 'active' : ''}`}
          onClick={toggleMobileMenu}
          aria-label="Toggle menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>

      {/* Desktop header */}
      <header className="bo-header desktop-only">
        <h1>{selectedSchool?.name}</h1>
        <div className="header-right">
          <div className="user-dropdown-container" ref={dropdownRef}>
            <button
              className="user-btn"
              onClick={() => setLanguageDropdownOpen(!languageDropdownOpen)}
              aria-label="User menu"
            >
              <User size={20} />
            </button>

            {languageDropdownOpen && (
              <div className="user-dropdown-menu">
                <div className="user-dropdown-header">
                  <span className="user-name">{user?.userName}</span>
                  <span className="user-role">{t(`common.${userType?.toLocaleLowerCase()}`)}</span>
                </div>

                <NavLink to="/profile" className="user-dropdown-item" onClick={() => setLanguageDropdownOpen(false)}>
                  <UserCircle size={16} />
                  {t('common.profile')}
                </NavLink>

                <button
                  className="user-dropdown-item"
                  onClick={() => setLanguageMenuOpen(!languageMenuOpen)}
                >
                  <Globe size={16} />
                  {t('common.language')}
                  <ChevronDown size={14} className={`chevron-icon ${languageMenuOpen ? 'open' : ''}`} />
                </button>

                {languageMenuOpen && (
                  <div className="language-submenu">
                    <button
                      className={`language-submenu-item ${currentLanguage === 'en' ? 'active' : ''}`}
                      onClick={() => changeLanguage('en')}
                    >
                      English
                    </button>
                    <button
                      className={`language-submenu-item ${currentLanguage === 'fr' ? 'active' : ''}`}
                      onClick={() => changeLanguage('fr')}
                    >
                      Français
                    </button>
                    <button
                      className={`language-submenu-item ${currentLanguage === 'ar' ? 'active' : ''}`}
                      onClick={() => changeLanguage('ar')}
                    >
                      العربية
                    </button>
                  </div>
                )}

                <button onClick={logout} className="user-dropdown-item danger">
                  <LogOut size={16} />
                  {t('common.logout')}
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Mobile menu overlay */}
      <div
        className={`bo-mobile-menu-overlay ${mobileMenuOpen ? 'active' : ''}`}
        onClick={closeMobileMenu}
      />

      <nav className={`bo-nav ${mobileMenuOpen ? 'open' : ''}`}>
        {/* Mobile-only menu header section */}
        <div className="mobile-menu-header">
          <div className="mobile-school-name">{selectedSchool?.name}</div>
          <div className="mobile-user-name">{user?.userName}</div>
        </div>

        <div className="bo-nav-items">
          {/* Courses - requires COURSES_R */}
          {hasRight('courses:r') && (
            <NavLink to="/courses" end onClick={closeMobileMenu}>{t('navigation.courses')}</NavLink>
          )}

          {/* Levels - requires LEVELS_R */}
          {hasRight('levels:r') && (
            <NavLink to="/levels" onClick={closeMobileMenu}>{t('navigation.levels')}</NavLink>
          )}

          {/* Classes - requires CLASSES_R */}
          {hasRight('classes:r') && (
            <NavLink to="/classes" onClick={closeMobileMenu}>{t('navigation.classes')}</NavLink>
          )}

          {/* Teachers - requires TEACHERS_R */}
          {hasRight('teachers:r') && (
            <NavLink to="/teachers" onClick={closeMobileMenu}>{t('navigation.teachers')}</NavLink>
          )}

          {/* Students - requires STUDENTS_R */}
          {hasRight('students:r') && (
            <NavLink to="/students" onClick={closeMobileMenu}>{t('navigation.students')}</NavLink>
          )}

          {/* Timetable - requires LESSONS_R */}
          {hasRight('lessons:r') && (
            <>
              {(userType === 'ADMIN' || isSuperAdmin) && (
                <NavLink to="/timetable" onClick={closeMobileMenu}>{t('navigation.timetable')}</NavLink>
              )}
              {userType === 'TEACHER' && (
                <NavLink to="/timetable/teacher" onClick={closeMobileMenu}>{t('navigation.myTimetable')}</NavLink>
              )}
              {userType === 'STUDENT' && (
                <NavLink to="/timetable/student" onClick={closeMobileMenu}>{t('navigation.myTimetable')}</NavLink>
              )}
            </>
          )}

          {hasRight('absences:r') && (
            <NavLink to="/absences" onClick={closeMobileMenu}>{t('navigation.absences')}</NavLink>
          )}

          {/* Bills - requires BILLS_R */}
          {hasRight('bills:r') && (
            <NavLink to="/bills" onClick={closeMobileMenu}>{t('navigation.bills')}</NavLink>
          )}

          {/* Administration - requires ADMINISTRATION_R */}
          {hasRight('administration:r') && (
            <NavLink to="/administration" onClick={closeMobileMenu}>{t('navigation.administration')}</NavLink>
          )}

          {/* Mobile Language Selector */}
          <div className="mobile-nav-item-container">
            <button
              className={`mobile-nav-item ${mobileLanguageOpen ? 'open' : ''}`}
              onClick={() => setMobileLanguageOpen(!mobileLanguageOpen)}
            >
              {t('common.language')} ({currentLanguage.toUpperCase()})
              <span className="arrow">▼</span>
            </button>
            {mobileLanguageOpen && (
              <div className="mobile-sub-nav">
                <button
                  className={`mobile-sub-item ${currentLanguage === 'fr' ? 'active' : ''}`}
                  onClick={() => changeLanguage('fr')}
                >
                  Français
                </button>
                <button
                  className={`mobile-sub-item ${currentLanguage === 'en' ? 'active' : ''}`}
                  onClick={() => changeLanguage('en')}
                >
                  English
                </button>
                <button
                  className={`mobile-sub-item ${currentLanguage === 'ar' ? 'active' : ''}`}
                  onClick={() => changeLanguage('ar')}
                >
                  العربية
                </button>
              </div>
            )}
          </div>

          {/* Mobile Logout */}
          <NavLink
            to="/profile"
            onClick={closeMobileMenu}
            className="mobile-nav-item"
          >
            {t('common.profile')}
          </NavLink>
          <button
            onClick={() => { logout(); closeMobileMenu(); }}
            className="mobile-nav-item mobile-logout-link"
          >
            {t('common.logout')}
          </button>
        </div>
      </nav>
      <main className="bo-content">
        <Outlet />
      </main>
    </div>
  );
};

export default BOLayout;

